import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../api/axios";
import Footer from "../components/Footer";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

const STATUS_CONFIG = {
  pending:    { label: "Pending",    color: "#e07b39", bg: "#fff4ec" },
  processing: { label: "Processing", color: "#c4a020", bg: "#fffbec" },
  shipped:    { label: "Shipped",    color: "#3a6ab0", bg: "#eef4ff" },
  delivered:  { label: "Delivered",  color: "#3a8a68", bg: "#eefaf4" },
  cancelled:  { label: "Cancelled",  color: "#c0392b", bg: "#fff0f0" },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "#888", bg: "#f5f5f5" };
  return (
    <span style={{ padding: "3px 10px", borderRadius: "999px", fontSize: "11px", fontWeight: "700", fontFamily: "Georgia, serif", color: cfg.color, background: cfg.bg }}>
      {cfg.label}
    </span>
  );
};

// ── ORDER DETAIL VIEW ─────────────────────────────────────────────────────────
const OrderDetail = ({ order, onBack, onCancelled }) => {
  const [cancelling, setCancelling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [error, setError] = useState("");

  const canCancel = ["pending", "processing"].includes(order.status);

  const handleCancel = async () => {
    setCancelling(true);
    setError("");
    try {
      const res = await api.post(`orders/${order.id}/cancel/`);
      onCancelled(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to cancel order.");
    } finally {
      setCancelling(false);
      setConfirmCancel(false);
    }
  };

  const steps = ["pending", "processing", "shipped", "delivered"];
  const currentStep = steps.indexOf(order.status);

  return (
    <div>
      {/* Back */}
      <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "12px", color: "#e07b39", letterSpacing: "0.05em", marginBottom: "20px", padding: 0 }}>
        ← BACK TO ORDERS
      </button>

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "24px" }}>
        <div>
          <h2 style={{ fontFamily: "Georgia, serif", fontSize: "22px", fontWeight: "700", color: "#1a1a1a" }}>
            Order #{order.id}
          </h2>
          <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#aaa", marginTop: "4px" }}>
            Placed on {new Date(order.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>
        <StatusBadge status={order.status} />
      </div>

      {/* Progress tracker — only show if not cancelled */}
      {order.status !== "cancelled" && (
        <div style={{ background: "#fff", borderRadius: "14px", padding: "24px", border: "1px solid #f0ebe3", marginBottom: "20px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            {steps.map((step, i) => (
              <div key={step} style={{ display: "flex", alignItems: "center", flex: i < steps.length - 1 ? 1 : "none" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
                  <div style={{ width: "32px", height: "32px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px", fontWeight: "700", fontFamily: "Georgia, serif", background: i <= currentStep ? "#e07b39" : "#f0ebe3", color: i <= currentStep ? "#fff" : "#ccc", transition: "all 0.3s" }}>
                    {i < currentStep ? "✓" : i + 1}
                  </div>
                  <span style={{ fontFamily: "Georgia, serif", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: i <= currentStep ? "#e07b39" : "#ccc", fontWeight: i === currentStep ? "700" : "400", textAlign: "center", whiteSpace: "nowrap" }}>
                    {step}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div style={{ flex: 1, height: "2px", margin: "0 6px", marginBottom: "20px", background: i < currentStep ? "#e07b39" : "#f0ebe3", transition: "background 0.3s" }} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Two column layout */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: "20px" }} className="order-detail-grid">
        {/* Left: items + summary */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{ background: "#fff", borderRadius: "14px", padding: "24px", border: "1px solid #f0ebe3" }}>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "16px" }}>Order Summary</p>
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              {order.items?.map((item) => (
                <div key={item.id} style={{ display: "flex", gap: "14px", alignItems: "center" }}>
                  <div style={{ width: "52px", height: "52px", borderRadius: "10px", background: "#f0ebe3", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px" }}>
                    🕯️
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "600", color: "#1a1a1a" }}>{item.product_name}</p>
                    <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa" }}>Qty: {item.quantity}</p>
                  </div>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", color: "#e07b39" }}>
                    {inr(parseFloat(item.price_at_purchase) * item.quantity)}
                  </p>
                </div>
              ))}
            </div>
            <div style={{ borderTop: "1px solid #f0ebe3", marginTop: "16px", paddingTop: "14px", display: "flex", flexDirection: "column", gap: "8px" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#888" }}>Subtotal</span>
                <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#1a1a1a" }}>{inr(order.total_price)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#888" }}>Shipping</span>
                <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#3a8a68" }}>Free</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "8px", borderTop: "1px solid #f0ebe3" }}>
                <span style={{ fontFamily: "Georgia, serif", fontSize: "14px", fontWeight: "700", color: "#1a1a1a" }}>Total</span>
                <span style={{ fontFamily: "Georgia, serif", fontSize: "16px", fontWeight: "700", color: "#e07b39" }}>{inr(order.total_price)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right: delivery info + cancel */}
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div style={{ background: "#fff", borderRadius: "14px", padding: "20px", border: "1px solid #f0ebe3" }}>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "12px" }}>Delivery Info</p>
            {order.shipping_address && (
              <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#555", lineHeight: "1.6", marginBottom: "10px" }}>{order.shipping_address}</p>
            )}
            {order.phone && (
              <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#555" }}>📞 {order.phone}</p>
            )}
            {order.payment_method && (
              <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#555", marginTop: "6px" }}>
                💳 {order.payment_method.charAt(0).toUpperCase() + order.payment_method.slice(1)}
              </p>
            )}
          </div>

          {/* Cancel */}
          {canCancel && (
            <div style={{ background: "#fff", borderRadius: "14px", padding: "20px", border: "1px solid #f0ebe3" }}>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "12px" }}>Need help?</p>
              {error && <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#c0392b", marginBottom: "10px" }}>⚠ {error}</p>}
              {!confirmCancel ? (
                <button onClick={() => setConfirmCancel(true)}
                  style={{ width: "100%", padding: "10px", borderRadius: "10px", border: "1.5px solid #e57373", background: "#fff", color: "#c0392b", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700" }}>
                  ✕ Cancel Order
                </button>
              ) : (
                <div>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#555", marginBottom: "12px" }}>Are you sure you want to cancel this order?</p>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button onClick={handleCancel} disabled={cancelling}
                      style={{ flex: 1, padding: "9px", borderRadius: "8px", border: "none", background: "#c0392b", color: "#fff", cursor: cancelling ? "not-allowed" : "pointer", fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700" }}>
                      {cancelling ? "..." : "Yes, Cancel"}
                    </button>
                    <button onClick={() => setConfirmCancel(false)}
                      style={{ flex: 1, padding: "9px", borderRadius: "8px", border: "1.5px solid #e8d5b8", background: "#fff", color: "#888", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "12px" }}>
                      Keep It
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`@media(max-width:640px){.order-detail-grid{grid-template-columns:1fr!important}}`}</style>
    </div>
  );
};

// ── MY ORDERS TAB ─────────────────────────────────────────────────────────────
const MyOrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    api.get("orders/").then(res => setOrders(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleCancelled = (updatedOrder) => {
    setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
    setSelectedOrder(updatedOrder);
  };

  if (selectedOrder) return <OrderDetail order={selectedOrder} onBack={() => setSelectedOrder(null)} onCancelled={handleCancelled} />;

  if (loading) return <p style={{ fontFamily: "Georgia, serif", color: "#aaa", fontSize: "13px" }}>Loading orders...</p>;

  if (orders.length === 0) return (
    <div style={{ textAlign: "center", padding: "60px 0" }}>
      <div style={{ fontSize: "48px", marginBottom: "16px" }}>🕯️</div>
      <p style={{ fontFamily: "Georgia, serif", fontSize: "15px", color: "#888" }}>No orders yet.</p>
    </div>
  );

  return (
    <div>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: "20px", fontWeight: "700", color: "#1a1a1a", marginBottom: "6px" }}>Order History</h2>
      <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#aaa", marginBottom: "24px" }}>Review your past purchases and track current shipments</p>

      {/* Table header */}
      <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 100px 110px 110px 110px", gap: "8px", padding: "10px 16px", background: "#f7f2eb", borderRadius: "10px", marginBottom: "8px" }}>
        {["ORDER ID", "DATE", "AMOUNT", "PAYMENT", "STATUS", "ACTION"].map(h => (
          <span key={h} style={{ fontFamily: "Georgia, serif", fontSize: "10px", fontWeight: "700", color: "#aaa", letterSpacing: "0.08em" }}>{h}</span>
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {orders.map(order => (
          <div key={order.id} style={{ display: "grid", gridTemplateColumns: "80px 1fr 100px 110px 110px 110px", gap: "8px", padding: "14px 16px", background: "#fff", borderRadius: "10px", border: "1px solid #f0ebe3", alignItems: "center" }}>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: "#e07b39" }}>#{order.id}</span>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#555" }}>
              {new Date(order.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
            </span>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: "#1a1a1a" }}>{inr(order.total_price)}</span>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#3a8a68", fontWeight: "600" }}>
              {order.payment_method ? order.payment_method.toUpperCase() : "—"}
            </span>
            <StatusBadge status={order.status} />
            <button onClick={() => setSelectedOrder(order)}
              style={{ padding: "7px 14px", borderRadius: "8px", background: "#e07b39", color: "#fff", border: "none", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "11px", fontWeight: "700", whiteSpace: "nowrap" }}>
              View Details
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── PROFILE INFO TAB ──────────────────────────────────────────────────────────
const ProfileInfoTab = ({ user, setUser }) => {
  const [editMode, setEditMode]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [success, setSuccess]     = useState("");
  const [errors, setErrors]       = useState({});

  const addr = user?.default_address || {};

  const [form, setForm] = useState({
    email:          user?.email          || "",
    contact_number: user?.contact_number || "",
    full_name:      addr.full_name       || "",
    phone:          addr.phone           || "",
    address_line_1: addr.address_line_1  || "",
    address_line_2: addr.address_line_2  || "",
    landmark:       addr.landmark        || "",
    city:           addr.city            || "",
    state:          addr.state           || "Maharashtra",
    postal_code:    addr.postal_code     || "",
  });

  const set = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
    setErrors(er => ({ ...er, [field]: "" }));
  };

  const handleSave = async () => {
    setSaving(true);
    setErrors({});
    setSuccess("");
    try {
      const res = await api.patch("users/me/update/", form);
      setUser(res.data);
      setEditMode(false);
      setSuccess("Profile updated successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === "object") setErrors(data);
      else setErrors({ api: "Failed to save. Please try again." });
    } finally { setSaving(false); }
  };

  const handleCancel = () => {
    const a = user?.default_address || {};
    setForm({
      email:          user?.email          || "",
      contact_number: user?.contact_number || "",
      full_name:      a.full_name          || "",
      phone:          a.phone             || "",
      address_line_1: a.address_line_1    || "",
      address_line_2: a.address_line_2    || "",
      landmark:       a.landmark          || "",
      city:           a.city              || "",
      state:          a.state             || "Maharashtra",
      postal_code:    a.postal_code       || "",
    });
    setErrors({});
    setEditMode(false);
  };

  const inputStyle = (field) => ({
    width: "100%", padding: "9px 12px", borderRadius: "9px",
    border: `1.5px solid ${errors[field] ? "#e57373" : "#e8d5b8"}`,
    background: "#fffaf4", fontFamily: "Georgia, serif", fontSize: "13px",
    outline: "none", boxSizing: "border-box",
  });

  const labelStyle = {
    display: "block", fontFamily: "Georgia, serif", fontSize: "10px",
    fontWeight: "700", color: "#aaa", textTransform: "uppercase",
    letterSpacing: "0.1em", marginBottom: "5px",
  };

  const errMsg = (field) => errors[field] && (
    <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#c0392b", marginTop: "3px" }}>
      ⚠ {errors[field]}
    </p>
  );

  return (
    <div>
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: "20px", fontWeight: "700", color: "#1a1a1a" }}>
          Profile Information
        </h2>
        {!editMode && (
          <button onClick={() => setEditMode(true)}
            style={{ padding: "8px 18px", borderRadius: "9px", background: "linear-gradient(135deg,#e07b39,#c96a18)", color: "#fff", border: "none", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", boxShadow: "0 3px 12px rgba(201,106,24,.25)" }}>
            ✏️ Edit Profile
          </button>
        )}
      </div>

      {/* Success toast */}
      {success && (
        <div style={{ background: "#eefaf4", border: "1px solid #b2dfdb", borderRadius: "10px", padding: "10px 16px", marginBottom: "16px", fontFamily: "Georgia, serif", fontSize: "12px", color: "#3a8a68" }}>
          ✅ {success}
        </div>
      )}

      {errors.api && (
        <div style={{ background: "#fff0f0", border: "1px solid #f5c6cb", borderRadius: "10px", padding: "10px 16px", marginBottom: "16px", fontFamily: "Georgia, serif", fontSize: "12px", color: "#c0392b" }}>
          ⚠ {errors.api}
        </div>
      )}

      <div style={{ background: "#fff", borderRadius: "14px", padding: "28px", border: "1px solid #f0ebe3" }}>

        {/* ── VIEW MODE ── */}
        {!editMode && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            {[
              { label: "Username",       value: user?.username,        note: "(cannot be changed)" },
              { label: "Email",          value: user?.email },
              { label: "Contact Number", value: user?.contact_number   || "—" },
              { label: "Role",           value: user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : "Customer" },
            ].map(({ label, value, note }) => (
              <div key={label}>
                <p style={labelStyle}>{label}</p>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "14px", color: "#1a1a1a", fontWeight: "600" }}>
                  {value || "—"}
                  {note && <span style={{ fontSize: "10px", color: "#bbb", marginLeft: "6px", fontWeight: "400" }}>{note}</span>}
                </p>
              </div>
            ))}

            {/* Address */}
            {user?.default_address ? (
              <>
                <div>
                  <p style={labelStyle}>Full Name</p>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "14px", color: "#1a1a1a", fontWeight: "600" }}>{user.default_address.full_name || "—"}</p>
                </div>
                <div>
                  <p style={labelStyle}>Address Phone</p>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "14px", color: "#1a1a1a", fontWeight: "600" }}>{user.default_address.phone || "—"}</p>
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <p style={labelStyle}>Shipping Address</p>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#555", lineHeight: "1.7" }}>
                    {[user.default_address.address_line_1, user.default_address.address_line_2, user.default_address.landmark, user.default_address.city, user.default_address.state, user.default_address.postal_code].filter(Boolean).join(", ")}
                  </p>
                </div>
              </>
            ) : (
              <div style={{ gridColumn: "1 / -1" }}>
                <p style={labelStyle}>Shipping Address</p>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#aaa" }}>{user?.shipping_address || "No address saved."}</p>
              </div>
            )}
          </div>
        )}

        {/* ── EDIT MODE ── */}
        {editMode && (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

            {/* Username — disabled */}
            <div>
              <label style={labelStyle}>Username <span style={{ color: "#bbb", textTransform: "none", fontSize: "10px" }}>(cannot be changed)</span></label>
              <input value={user?.username || ""} disabled
                style={{ ...inputStyle(), background: "#f5f5f5", color: "#bbb", cursor: "not-allowed" }} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
              <div>
                <label style={labelStyle}>Email *</label>
                <input value={form.email} onChange={set("email")} style={inputStyle("email")} placeholder="your@email.com" />
                {errMsg("email")}
              </div>
              <div>
                <label style={labelStyle}>Contact Number *</label>
                <input value={form.contact_number} onChange={set("contact_number")} style={inputStyle("contact_number")} placeholder="10-digit mobile" />
                {errMsg("contact_number")}
              </div>
            </div>

            <div style={{ borderTop: "1px solid #f0ebe3", paddingTop: "16px" }}>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", fontWeight: "700", color: "#5a3a1a", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "14px" }}>
                Shipping Address
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                <div>
                  <label style={labelStyle}>Full Name</label>
                  <input value={form.full_name} onChange={set("full_name")} style={inputStyle("full_name")} placeholder="Recipient full name" />
                </div>
                <div>
                  <label style={labelStyle}>Address Phone</label>
                  <input value={form.phone} onChange={set("phone")} style={inputStyle("phone")} placeholder="Phone for delivery" />
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={labelStyle}>Address Line 1 *</label>
                  <input value={form.address_line_1} onChange={set("address_line_1")} style={inputStyle("address_line_1")} placeholder="House/flat no., street name" />
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={labelStyle}>Address Line 2</label>
                  <input value={form.address_line_2} onChange={set("address_line_2")} style={inputStyle("address_line_2")} placeholder="Area, colony (optional)" />
                </div>
                <div>
                  <label style={labelStyle}>Landmark</label>
                  <input value={form.landmark} onChange={set("landmark")} style={inputStyle("landmark")} placeholder="Near... (optional)" />
                </div>
                <div>
                  <label style={labelStyle}>City</label>
                  <input value={form.city} onChange={set("city")} style={inputStyle("city")} placeholder="City" />
                </div>
                <div>
                  <label style={labelStyle}>State</label>
                  <select value={form.state} onChange={set("state")} style={inputStyle("state")}>
                    {["Maharashtra","Delhi","Karnataka","Tamil Nadu","Telangana","Gujarat","Rajasthan","Uttar Pradesh","West Bengal","Kerala","Madhya Pradesh","Punjab","Haryana"].map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Postal Code</label>
                  <input value={form.postal_code} onChange={set("postal_code")} style={inputStyle("postal_code")} placeholder="6-digit PIN" maxLength={6} />
                </div>
              </div>
            </div>

            {/* Save / Cancel */}
            <div style={{ display: "flex", gap: "10px", marginTop: "8px" }}>
              <button onClick={handleCancel}
                style={{ padding: "10px 22px", borderRadius: "10px", border: "1.5px solid #e8d5b8", background: "transparent", color: "#888", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "13px" }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                style={{ flex: 1, padding: "10px", borderRadius: "10px", background: saving ? "#e0a870" : "linear-gradient(135deg,#e07b39,#c96a18)", color: "#fff", border: "none", cursor: saving ? "not-allowed" : "pointer", fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", boxShadow: "0 4px 14px rgba(201,106,24,.25)" }}>
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// ── DASHBOARD TAB ─────────────────────────────────────────────────────────────
const DashboardTab = ({ user, orders, onNavigate }) => {
  const totalOrders = orders.length;
  const totalSpent = orders
    .filter(o => o.status !== 'cancelled')
    .reduce((s, o) => s + parseFloat(o.total_price || 0), 0);
  const pending   = orders.filter(o => o.status === "pending").length;
  const active    = orders.filter(o => !["cancelled", "delivered"].includes(o.status)).length;

  return (
    <div>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: "20px", fontWeight: "700", color: "#1a1a1a", marginBottom: "6px" }}>
        Welcome back, {user?.username} 👋
      </h2>
      <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#aaa", marginBottom: "28px" }}>Here's a summary of your account</p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", marginBottom: "28px" }}>
        {[
          { label: "Total Orders", value: totalOrders, icon: "📦" },
          { label: "Amount Spent", value: inr(totalSpent), icon: "💰", sub: "excl. cancelled" },
          { label: "Pending", value: pending, icon: "⏳" },
          { label: "Active", value: active, icon: "🔄" },
        ].map(({ label, value, icon }) => (
          <div key={label} style={{ background: "#fff", borderRadius: "14px", padding: "20px", border: "1px solid #f0ebe3", textAlign: "center" }}>
            <div style={{ fontSize: "28px", marginBottom: "8px" }}>{icon}</div>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "18px", fontWeight: "700", color: "#e07b39", marginBottom: "4px" }}>{value}</p>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa" }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Recent orders preview */}
      {orders.length > 0 && (
        <div style={{ background: "#fff", borderRadius: "14px", padding: "20px", border: "1px solid #f0ebe3" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", color: "#1a1a1a" }}>Recent Orders</p>
            <button onClick={() => onNavigate("orders")} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "11px", color: "#e07b39" }}>View all →</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {orders.slice(0, 3).map(o => (
              <div key={o.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f7f2eb" }}>
                <div>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "600", color: "#1a1a1a" }}>Order #{o.id}</p>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa" }}>{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: "#e07b39" }}>{inr(o.total_price)}</p>
                  <StatusBadge status={o.status} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── MAIN PROFILE PAGE ─────────────────────────────────────────────────────────
const Profile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "dashboard");
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userRes, ordersRes] = await Promise.all([
          api.get("users/me/"),
          api.get("orders/"),
        ]);
        setUser(userRes.data);
        setOrders(ordersRes.data);
      } catch {}
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    navigate("/login");
  };

  const NAV = [
    { id: "dashboard", label: "Dashboard",     icon: "⊞" },
    { id: "profile",   label: "Profile Info",  icon: "👤" },
    { id: "orders",    label: "Order History", icon: "📦" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#faf8f3", paddingBottom: "40px" }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "40px 20px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: "24px", marginBottom: "40px" }} className="profile-grid">
          {/* Sidebar */}
          <div style={{ background: "#fff", borderRadius: "16px", padding: "24px", border: "1px solid #f0ebe3", height: "fit-content" }}>
            {/* Avatar */}
            <div style={{ textAlign: "center", marginBottom: "24px", paddingBottom: "20px", borderBottom: "1px solid #f0ebe3" }}>
              <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: "linear-gradient(135deg, #e07b39, #c96a18)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px", fontSize: "26px" }}>
                🕯️
              </div>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "14px", fontWeight: "700", color: "#1a1a1a" }}>{user?.username || "..."}</p>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa", marginTop: "2px" }}>{user?.email || ""}</p>
            </div>

            {/* Nav */}
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {NAV.map(({ id, label, icon }) => (
                <button key={id} onClick={() => setActiveTab(id)}
                  style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", borderRadius: "10px", border: "none", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "13px", textAlign: "left", background: activeTab === id ? "#fff4ec" : "transparent", color: activeTab === id ? "#e07b39" : "#555", fontWeight: activeTab === id ? "700" : "400", transition: "all 0.15s" }}>
                  <span>{icon}</span> {label}
                </button>
              ))}
            </div>

            <div style={{ borderTop: "1px solid #f0ebe3", marginTop: "16px", paddingTop: "12px" }}>
              <button onClick={handleLogout}
                style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", borderRadius: "10px", border: "none", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "13px", color: "#c0392b", background: "transparent", width: "100%" }}>
                🚪 Log Out
              </button>
            </div>
          </div>

          {/* ── Main Content ── */}
          <div style={{ background: "#fff", borderRadius: "16px", padding: "28px", border: "1px solid #f0ebe3", minHeight: "400px" }}>
            {loading ? (
              <p style={{ color: "#aaa", fontFamily: "Georgia, serif", fontSize: "13px" }}>Loading...</p>
            ) : (
              <>
                {activeTab === "dashboard" && <DashboardTab user={user} orders={orders} onNavigate={setActiveTab} />}
                {activeTab === "profile"   && <ProfileInfoTab user={user} setUser={setUser} />}
                {activeTab === "orders"    && <MyOrdersTab />}
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
      <style>{`@media(max-width:700px){.profile-grid{grid-template-columns:1fr!important}}`}</style>
    </div>
  );
};

export default Profile;